import Store from '@ember-data/store';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import CampaignModel from 'donor-elf-web/models/campaign';
import AddSomethingModal, {
  type OpenArgs,
} from 'donor-elf-web/services/add-something-modal';
import CampaignService from 'donor-elf-web/services/campaign-service';
import Changeset from 'ember-changeset';
import lookupValidator from 'ember-changeset-validations';
import type { ValidationResult } from 'ember-changeset/types';

interface ComponentArgs {
  openArgs?: OpenArgs;
}

export default class AddSomethingModalCampaignIndexComponent extends Component<ComponentArgs> {
  constructor(owner: any, args: ComponentArgs) {
    super(owner, args);
    this.createChangeset();
  }

  @service declare addSomethingModal: AddSomethingModal;
  @service declare store: Store;
  @service declare campaignService: CampaignService;

  @tracked changeset: any;
  @tracked showValidationErrors = false;
  @tracked saving = false;
  @tracked saveError?: any;

  get existingCampaign() {
    return this.args.openArgs?.campaignOptions?.existingCampaign;
  }

  get newCampaign() {
    return this.existingCampaign == null;
  }

  // parent campaign can't be changed here for existing pledges
  get showParentCampaignDropdown() {
    return this.newCampaign && this.campaignService.campaigns.length > 0;
  }

  get parentCampaignSelectOptions() {
    return [
      { val: undefined, label: 'None' },
      ...this.campaignService.sortedCampaignSelectOptions,
    ];
  }

  get campaignValidation() {
    return {
      name: this.validateName,
      parentCampaignId: this.validateParentCampaignId,
    };
  }

  validateName = (
    _key: string,
    newValue: string,
    _oldValue: string,
    _changes: any,
    content: CampaignModel,
  ): ValidationResult => {
    if (isEmpty(newValue)) {
      return "Can't be blank";
    }
    if (content.restricted) {
      return "Can't change the name of a restricted campaign";
    }
    return true;
  };

  validateParentCampaignId = (
    _key: string,
    newValue: string,
    _oldValue: string,
    _changes: any,
    _content: any,
  ): ValidationResult => {
    let newParentCampaign = this.campaignService.campaignsById[newValue];
    if (newParentCampaign?.restricted) {
      return "Can't create a child campaign on a restricted campaign. That functionality is coming soon.";
    }
    return true;
  };

  createChangeset() {
    let campaign = this.newCampaign
      ? this.store.createRecord('campaign')
      : this.args.openArgs!.campaignOptions!.existingCampaign!;

    let { campaignValidation } = this;
    this.changeset = new Changeset(
      campaign,
      lookupValidator(campaignValidation),
      campaignValidation,
    );

    if (
      this.newCampaign &&
      this.args.openArgs!.campaignOptions?.parentCampaignId
    ) {
      this.changeset.set(
        'parentCampaignId',
        this.args.openArgs!.campaignOptions.parentCampaignId,
      );
    }
  }

  save = async () => {
    if (this.saving) {
      return;
    }
    this.showValidationErrors = false;
    await this.changeset.validate();
    if (this.changeset.get('isValid')) {
      try {
        this.saving = true;
        await this.changeset.save();
        let campaign = this.changeset.get('data') as CampaignModel;
        this.campaignService.onCampaignSaved({
          campaign,
          newCampaign: this.newCampaign,
        });
        this.addSomethingModal.close();
      } catch (e) {
        this.saveError = e;
      }
      this.saving = false;
    } else {
      this.showValidationErrors = true;
    }
  };

  close = () => {
    this.addSomethingModal.close();
  };

  writeFindCampaignInRubyToClipboard = () => {
    window.navigator.clipboard.writeText(
      `campaign = Campaign.find('${this.existingCampaign?.id}')`,
    );
  };
}
